import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-';
const fmtShort = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '-';

const STATUS_LABEL = {
  confirmed: { label: 'Confirmée',  color: '#0F6E56', bg: '#E8F5F0' },
  pending:   { label: 'En attente', color: '#C4784A', bg: '#FDF8F4' },
  cancelled: { label: 'Annulée',   color: '#A32D2D', bg: '#FCEBEB' },
  completed: { label: 'Terminée',  color: '#534AB7', bg: '#EEEDFE' },
};

export default function OwnerPortalPage({ ownerId }) {
  const [owner,        setOwner]        = useState(null);
  const [listings,     setListings]     = useState([]);
  const [bookings,     setBookings]     = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [tab,          setTab]          = useState('overview');

  const now = new Date();
  const [monthOffset, setMonthOffset] = useState(0);
  const [calOffset,   setCalOffset]   = useState(0);

  const targetDate  = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const monthLabel  = targetDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const monthStart  = targetDate.toISOString().slice(0, 7) + '-01';
  const monthEnd    = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).toISOString().slice(0, 10);

  const calDate     = new Date(now.getFullYear(), now.getMonth() + calOffset, 1);
  const calLabel    = calDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const calYear     = calDate.getFullYear();
  const calMonth    = calDate.getMonth();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const todayStr    = now.toISOString().slice(0, 10);

  useEffect(() => {
    if (!ownerId) { setError('Lien invalide.'); setLoading(false); return; }
    (async () => {
      try {
        const { data: ownerData, error: ownerErr } = await supabase
          .from('property_owners').select('*').eq('id', ownerId).single();
        if (ownerErr || !ownerData) throw new Error('Propriétaire introuvable.');
        setOwner(ownerData);

        const { data: listingsData } = await supabase
          .from('listings').select('*').eq('owner_id', ownerId);
        setListings(listingsData || []);

        const listingIds = (listingsData || []).map(l => l.id);
        if (listingIds.length > 0) {
          const [{ data: bookingsData }, { data: blockedData }] = await Promise.all([
            supabase.from('bookings').select('*').in('listing_id', listingIds).order('date_start', { ascending: false }),
            supabase.from('blocked_dates').select('*').in('listing_id', listingIds),
          ]);
          setBookings(bookingsData || []);
          setBlockedDates(blockedData || []);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [ownerId]);

  const bookingsOfMonth = bookings.filter(b =>
    b.date_start >= monthStart && b.date_start <= monthEnd && b.status !== 'cancelled'
  );

  const totalRevenue  = bookings.filter(b => b.status !== 'cancelled').reduce((s, b) => s + (b.host_payout || 0), 0);
  const monthRevenue  = bookingsOfMonth.reduce((s, b) => s + (b.host_payout || 0), 0);
  const monthNights   = bookingsOfMonth.reduce((s, b) => s + (b.nights || 0), 0);
  const monthBookings = bookingsOfMonth.length;

  const upcoming = bookings.filter(b => b.date_end >= todayStr && b.status === 'confirmed')
    .sort((a, b) => a.date_start.localeCompare(b.date_start));

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--green-light)', borderTop: '3px solid var(--green)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ fontSize: 14, color: 'var(--ink-3)', fontFamily: 'var(--sans)' }}>Chargement de votre portail...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--surface)', padding: '2rem' }}>
      <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 16, padding: '2rem', maxWidth: 400, textAlign: 'center' }}>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 20, marginBottom: 8, color: '#A32D2D' }}>Lien invalide</h2>
        <p style={{ fontSize: 14, color: '#A32D2D', lineHeight: 1.6 }}>{error}</p>
        <p style={{ fontSize: 13, color: '#A32D2D', marginTop: 12 }}>Contactez votre conciergerie pour obtenir un nouveau lien.</p>
      </div>
    </div>
  );

  const card = { background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 };
  const toKey = (d) => `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', fontFamily: 'var(--sans)' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '0 1.5rem' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 0 0' }}>
            <div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.5px' }}>
                haven<span style={{ color: 'var(--green)' }}>ly</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 2 }}>Portail propriétaire</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{owner?.full_name}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>{owner?.email}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 0, marginTop: 14, overflowX: 'auto' }}>
            {[
              { key: 'overview', label: "Vue d'ensemble" },
              { key: 'calendar', label: 'Calendrier' },
              { key: 'bookings', label: 'Réservations' },
              { key: 'revenues', label: 'Revenus' },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{ padding: '10px 18px', background: 'none', border: 'none', borderBottom: tab === t.key ? '2px solid var(--ink)' : '2px solid transparent', fontSize: 13, fontWeight: tab === t.key ? 500 : 400, color: tab === t.key ? 'var(--ink)' : 'var(--ink-4)', cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '1.5rem' }}>

        {/* ═══ VUE D'ENSEMBLE ═══ */}
        {tab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ ...card }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontSize: 15, fontWeight: 500 }}>
                  {monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setMonthOffset(o => o - 1)}
                    style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                  <button onClick={() => setMonthOffset(o => Math.min(0, o + 1))} disabled={monthOffset === 0}
                    style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: monthOffset === 0 ? 'var(--surface)' : 'var(--white)', cursor: monthOffset === 0 ? 'not-allowed' : 'pointer', fontSize: 16, color: monthOffset === 0 ? 'var(--ink-4)' : 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                {[
                  { label: 'Revenus perçus', val: fmt(monthRevenue), color: 'var(--green-mid)', sub: 'après commission' },
                  { label: 'Réservations',   val: monthBookings,     sub: 'ce mois' },
                  { label: 'Nuits louées',   val: monthNights,       sub: 'ce mois' },
                ].map(stat => (
                  <div key={stat.label} style={{ background: 'var(--surface)', borderRadius: 10, padding: '12px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 90 }}>
                    <div style={{ fontSize: 10, color: 'var(--ink-4)', lineHeight: 1.3, marginBottom: 6 }}>{stat.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 500, color: stat.color || 'var(--ink)' }}>{stat.val}</div>
                    <div style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 4 }}>{stat.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {upcoming.length > 0 && (
              <div style={card}>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 14 }}>Prochaines arrivées</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {upcoming.slice(0, 3).map(b => {
                    const listing = listings.find(l => l.id === b.listing_id);
                    const st = STATUS_LABEL[b.status] || STATUS_LABEL.confirmed;
                    return (
                      <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--surface)', borderRadius: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {listing?.title || 'Bien inconnu'}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
                            {fmtShort(b.date_start)} → {fmtShort(b.date_end)} · {b.nights} nuit{b.nights > 1 ? 's' : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, marginLeft: 12 }}>
                          <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: st.bg, color: st.color }}>{st.label}</span>
                          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--green-mid)' }}>{fmt(b.host_payout)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 14 }}>Mes biens ({listings.length})</div>
              {listings.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--ink-4)', textAlign: 'center', padding: '20px 0' }}>Aucun bien associé pour l'instant.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {listings.map(l => (
                    <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px', background: 'var(--surface)', borderRadius: 10 }}>
                      {l.photos?.[0] && (
                        <img src={l.photos[0]} alt="" style={{ width: 56, height: 46, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 2 }}>{l.type} · {l.city} · {l.price_night} €/nuit</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <a href={`${window.location.origin}/?listing=${l.id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: '#EEEDFE', color: '#534AB7', textDecoration: 'none', fontWeight: 500 }}>
                          Voir l'annonce →
                        </a>
                        <div style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: l.status === 'active' ? 'var(--green-light)' : '#FDF0E6', color: l.status === 'active' ? 'var(--green-mid)' : '#C4784A' }}>
                          {l.status === 'active' ? 'En ligne' : 'En attente'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 14, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, color: 'var(--green-mid)', marginBottom: 4 }}>Total cumulé reçu depuis Havenly</div>
                <div style={{ fontSize: 11, color: 'var(--green-mid)', opacity: 0.8 }}>Toutes réservations confondues</div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--green-mid)' }}>{fmt(totalRevenue)}</div>
            </div>

            <div style={{ ...card, textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6 }}>Une question ?</div>
              <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.6 }}>
                Votre conciergerie gère votre bien au quotidien. Contactez-nous pour tout renseignement.
              </p>
              <a href="mailto:contact@havenly.fr"
                style={{ display: 'inline-block', background: 'var(--green)', color: '#fff', padding: '10px 24px', borderRadius: 24, textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>
                Contacter ma conciergerie
              </a>
            </div>
          </div>
        )}

        {/* ═══ CALENDRIER ═══ */}
        {tab === 'calendar' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Légende */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              {[
                { color: '#1D9E75', label: 'Réservée' },
                { color: '#E8A23D', label: 'En attente' },
                { color: '#D1D5DB', label: 'Bloquée' },
              ].map(l => (
                <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ink-3)' }}>
                  <div style={{ width: 24, height: 12, borderRadius: 4, background: l.color }} />
                  {l.label}
                </div>
              ))}
            </div>

            {/* Navigation mois */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button onClick={() => setCalOffset(o => o - 1)}
                style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
              <div style={{ fontSize: 15, fontWeight: 500, minWidth: 180, textAlign: 'center', textTransform: 'capitalize' }}>
                {calLabel.charAt(0).toUpperCase() + calLabel.slice(1)}
              </div>
              <button onClick={() => setCalOffset(o => o + 1)}
                style={{ width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
            </div>

            {listings.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', padding: '3rem' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>📅</div>
                <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Aucun bien associé pour le moment.</div>
              </div>
            ) : listings.map((listing, lIdx) => {
              const COLORS = ['#1D9E75', '#534AB7', '#C4784A', '#1A73E8'];
              const color = COLORS[lIdx % COLORS.length];
              const lBookings = bookings.filter(b =>
                b.listing_id === listing.id && ['confirmed', 'pending'].includes(b.status)
              );
              const lBlocked = blockedDates.filter(b => b.listing_id === listing.id);
              const firstDay = new Date(calYear, calMonth, 1).getDay();
              const offset   = firstDay === 0 ? 6 : firstDay - 1;
              const calStart = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-01`;
              const calEnd   = new Date(calYear, calMonth + 1, 0).toISOString().slice(0, 10);
              const monthBooks = lBookings.filter(b => b.date_start <= calEnd && b.date_end >= calStart);

              return (
                <div key={listing.id} style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>

                  {/* Header bien */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid var(--border)', borderLeft: `4px solid ${color}` }}>
                    {listing.photos?.[0] && (
                      <img src={listing.photos[0]} alt="" style={{ width: 52, height: 42, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginBottom: 2 }}>{listing.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>
                        {listing.type} · {listing.city} · {listing.price_night} €/nuit · {listing.guests_max || listing.guests_max} voyageurs max
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                      <a href={`${window.location.origin}/?listing=${listing.id}`} target="_blank" rel="noreferrer"
                        style={{ fontSize: 11, padding: '4px 12px', borderRadius: 20, background: '#EEEDFE', color: '#534AB7', textDecoration: 'none', fontWeight: 500, whiteSpace: 'nowrap' }}>
                        Voir l'annonce →
                      </a>
                      <span style={{ fontSize: 11, fontWeight: 500, padding: '4px 10px', borderRadius: 20, background: listing.status === 'active' ? 'var(--green-light)' : '#FDF0E6', color: listing.status === 'active' ? 'var(--green-mid)' : '#C4784A' }}>
                        {listing.status === 'active' ? 'En ligne' : 'En attente'}
                      </span>
                    </div>
                  </div>

                  {/* Grille calendrier */}
                  <div style={{ padding: '16px 20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 8 }}>
                      {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                        <div key={d} style={{ fontSize: 11, color: 'var(--ink-4)', textAlign: 'center', fontWeight: 500 }}>{d}</div>
                      ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                      {Array.from({ length: offset }).map((_, i) => <div key={`e${i}`} />)}
                      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => {
                        const key       = toKey(d);
                        const isPast    = key < todayStr;
                        const isToday   = key === todayStr;
                        const booking   = lBookings.find(b => b.date_start <= key && b.date_end > key);
                        const isBlocked = lBlocked.some(b => b.date === key);
                        const isStart   = booking?.date_start === key;
                        const isEnd     = booking?.date_end === key;
                        const isPending = booking?.status === 'pending';

                        let bg = 'var(--white)';
                        let textColor = isPast ? 'var(--ink-4)' : 'var(--ink)';
                        let borderRadius = 6;

                        if (isToday && !booking) { bg = 'var(--green-light)'; textColor = 'var(--green-mid)'; }
                        if (isBlocked && !booking) { bg = '#F3F4F6'; textColor = '#9CA3AF'; }
                        if (booking) {
                          bg = isPending ? '#FDF0E6' : '#E8F5F0';
                          textColor = isPending ? '#C4784A' : '#0F6E56';
                          if (isStart && isEnd) borderRadius = 6;
                          else if (isStart) borderRadius = '6px 0 0 6px';
                          else if (isEnd) borderRadius = '0 6px 6px 0';
                          else borderRadius = 0;
                        }

                        return (
                          <div key={d}
                            title={booking ? `${isPending ? 'En attente' : 'Confirmée'} — ${fmtShort(booking.date_start)} → ${fmtShort(booking.date_end)}` : isBlocked ? 'Date bloquée' : ''}
                            style={{ aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', borderRadius, background: bg, color: textColor, fontSize: 13, fontWeight: isToday ? 700 : 400 }}>
                            {isBlocked && !booking ? (
                              <div style={{ position: 'absolute', inset: 2, borderRadius: 4, background: '#D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                                <span style={{ fontSize: 11, color: '#9CA3AF' }}>{d}</span>
                              </div>
                            ) : d}
                            {isStart && !isBlocked && (
                              <span style={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', background: isPending ? '#C4784A' : '#1D9E75' }} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Réservations du mois */}
                  {monthBooks.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '14px 20px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Réservations ce mois
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {monthBooks.map(b => {
                          const st = STATUS_LABEL[b.status] || STATUS_LABEL.confirmed;
                          const n = b.nights || Math.round((new Date(b.date_end) - new Date(b.date_start)) / 86400000);
                          return (
                            <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--surface)', borderRadius: 10, gap: 12 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
                                  {fmtShort(b.date_start)} → {fmtShort(b.date_end)}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>
                                  {n} nuit{n > 1 ? 's' : ''} · {b.guests || 1} voyageur{(b.guests || 1) > 1 ? 's' : ''}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: st.bg, color: st.color }}>{st.label}</span>
                                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--green-mid)' }}>{fmt(b.host_payout)}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Équipements */}
                  {listing.amenities?.length > 0 && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '14px 20px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Équipements
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {listing.amenities.map(a => (
                          <span key={a} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: 'var(--surface)', color: 'var(--ink-3)', border: '1px solid var(--border)' }}>{a}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {listing.description && (
                    <div style={{ borderTop: '1px solid var(--border)', padding: '14px 20px' }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Description
                      </div>
                      <p style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.7, margin: 0 }}>{listing.description}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ RÉSERVATIONS ═══ */}
        {tab === 'bookings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ ...card, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>
                {monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setMonthOffset(o => o - 1)}
                  style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--white)', cursor: 'pointer', fontSize: 16, color: 'var(--ink-3)' }}>‹</button>
                <button onClick={() => setMonthOffset(o => Math.min(0, o + 1))} disabled={monthOffset === 0}
                  style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: monthOffset === 0 ? 'var(--surface)' : 'var(--white)', cursor: monthOffset === 0 ? 'not-allowed' : 'pointer', fontSize: 16, color: monthOffset === 0 ? 'var(--ink-4)' : 'var(--ink-3)' }}>›</button>
              </div>
            </div>

            {bookingsOfMonth.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Aucune réservation ce mois-ci.</div>
              </div>
            ) : bookingsOfMonth.map(b => {
              const listing = listings.find(l => l.id === b.listing_id);
              const st = STATUS_LABEL[b.status] || STATUS_LABEL.confirmed;
              return (
                <div key={b.id} style={card}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{listing?.title || 'Bien inconnu'}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 8 }}>
                        {fmtDate(b.date_start)} → {fmtDate(b.date_end)}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, background: 'var(--surface)', color: 'var(--ink-3)', padding: '3px 10px', borderRadius: 20 }}>
                          {b.nights} nuit{b.nights > 1 ? 's' : ''}
                        </span>
                        <span style={{ fontSize: 12, background: 'var(--surface)', color: 'var(--ink-3)', padding: '3px 10px', borderRadius: 20 }}>
                          {b.guests || 1} voyageur{(b.guests || 1) > 1 ? 's' : ''}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 10px', borderRadius: 20, background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 500, color: 'var(--green-mid)' }}>{fmt(b.host_payout)}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>votre part</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>Total : {fmt(b.price_total)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ═══ REVENUS ═══ */}
        {tab === 'revenues' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={card}>
              <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 16 }}>Historique des 3 derniers mois</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[-2, -1, 0].map(offset => {
                  const d      = new Date(now.getFullYear(), now.getMonth() + offset, 1);
                  const start  = d.toISOString().slice(0, 7) + '-01';
                  const end    = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
                  const label  = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
                  const boks   = bookings.filter(b => b.date_start >= start && b.date_start <= end && b.status !== 'cancelled');
                  const rev    = boks.reduce((s, b) => s + (b.host_payout || 0), 0);
                  const nights = boks.reduce((s, b) => s + (b.nights || 0), 0);
                  const isCurrent = offset === 0;
                  return (
                    <div key={offset} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: isCurrent ? 'var(--green-light)' : 'var(--surface)', borderRadius: 10, border: isCurrent ? '1px solid var(--green)' : '1px solid transparent' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: isCurrent ? 500 : 400, color: isCurrent ? 'var(--green-mid)' : 'var(--ink)' }}>
                          {label.charAt(0).toUpperCase() + label.slice(1)}
                          {isCurrent && <span style={{ fontSize: 11, marginLeft: 8, background: 'var(--green)', color: '#fff', padding: '1px 8px', borderRadius: 20 }}>En cours</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{boks.length} réservation{boks.length !== 1 ? 's' : ''} · {nights} nuit{nights !== 1 ? 's' : ''}</div>
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 500, color: isCurrent ? 'var(--green-mid)' : 'var(--ink)' }}>{fmt(rev)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 14, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--green-mid)' }}>Total cumulé</div>
                <div style={{ fontSize: 11, color: 'var(--green-mid)', opacity: 0.8, marginTop: 2 }}>
                  {bookings.filter(b => b.status !== 'cancelled').length} réservation{bookings.filter(b => b.status !== 'cancelled').length !== 1 ? 's' : ''} au total
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 500, color: 'var(--green-mid)' }}>{fmt(totalRevenue)}</div>
            </div>

            {listings.length > 1 && (
              <div style={card}>
                <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 14 }}>Revenus par bien</div>
                {listings.map(l => {
                  const lBookings = bookings.filter(b => b.listing_id === l.id && b.status !== 'cancelled');
                  const lRevenue  = lBookings.reduce((s, b) => s + (b.host_payout || 0), 0);
                  return (
                    <div key={l.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{l.title}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{lBookings.length} réservation{lBookings.length !== 1 ? 's' : ''}</div>
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--green-mid)' }}>{fmt(lRevenue)}</div>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ background: '#FDF8F4', border: '1px solid #E8A87C', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#C4784A', marginBottom: 6 }}>Transparence Havenly</div>
              <p style={{ fontSize: 12, color: '#8B5A3A', lineHeight: 1.65, margin: 0 }}>
                Vos revenus sont versés directement sur votre compte Stripe après chaque séjour. La commission Havenly (8%) et les honoraires de votre conciergerie sont prélevés automatiquement. Vous ne dépendez d'aucun intermédiaire.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
